// src/components/sidebar.tsx
import { SideLink } from '@/data/sidelinks'
import { getSidebarText } from '@/hooks/common/common-functions'
import useLocalStorage, {
  useLocalStorageString,
} from '@/hooks/use-local-storage'
import { cn } from '@/lib/utils'
import { AccountType } from '@/pages/auth/components/user-auth-form'
import { IconChevronsLeft, IconMenu2, IconX } from '@tabler/icons-react'
import { HTMLAttributes, useEffect, useMemo, useState } from 'react'
import { Button } from './custom/button'
import './index.css'
import Nav from './nav'
import { iconObj } from '@/hooks/common/get-icons'
import { Settings, HardDrive } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { onboardingSteps } from '@/data/onboarding-steps'
import { useOnboardingStorage } from '@/hooks/use-onboarding-storage'
import { OnboardingTooltip } from './onboarding-tooltip'

interface SidebarProps extends HTMLAttributes<HTMLElement> {
  isCollapsed: boolean
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>
}

// Helper function to get dashboard path based on account type
const getDashboardPath = (accountType: AccountType): string => {
  switch (accountType) {
    case AccountType.client:
    case AccountType.app_client:
      return '/client-portal/dashboard'

    case AccountType.business_team_leader:
    case AccountType.team_leader:
    case AccountType.staff_team_leader:
      return '/team-leader/dashboard'

    case AccountType.business_operations_managers:
    case AccountType.business_operations_manager:
    case AccountType.staff_operations_manager:
    case AccountType.operations_manager:
      return '/operations-manager/dashboard'

    case AccountType.business:
    case AccountType.business_admin:
    default:
      return '/'
  }
}

export default function Sidebar({
  className,
  isCollapsed,
  setIsCollapsed,
}: SidebarProps) {
  const navigate = useNavigate();
  const [navOpened, setNavOpened] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [isPWA, setIsPWA] = useState(false)

  const accountType = useLocalStorageString('accountType') as AccountType
  const [sidebar] = useLocalStorage<SideLink[]>({
    defaultValue: [],
    key: 'sidebarLinks',
  })

  // Onboarding Integration
  const businessId = localStorage.getItem('businessId') || 'default';
  const { 
    isActive: isOnboardingActive, 
    currentStep, 
    saveProgress, 
    completeOnboarding
  } = useOnboardingStorage(businessId);

  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [highlightedElement, setHighlightedElement] = useState<Element | null>(null);
  const [elementFound, setElementFound] = useState(false);

  // Check if app is running as PWA
  useEffect(() => {
    const isInStandaloneMode = () => 
      window.matchMedia('(display-mode: standalone)').matches || 
      (window.navigator as any).standalone === true;
    
    setIsPWA(isInStandaloneMode());
  }, []);

  const sidebarLinks: SideLink[] = useMemo(() => {
    return sidebar.map((item) => ({
      ...item,
      icon: iconObj[item.href],
      sub: item.sub?.map((subItem) => ({
        ...subItem,
        icon: iconObj[subItem.href] ?? <Settings size={18} />,
      })),
    }))
  }, [sidebar])

  useEffect(() => {
    if (navOpened) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }
  }, [navOpened])

  // Calculate tooltip position and highlight elements with retry logic
  useEffect(() => {
    if (isOnboardingActive && onboardingSteps[currentStep]) {
      const step = onboardingSteps[currentStep];
      
      // Remove previous highlight
      if (highlightedElement) {
        highlightedElement.classList.remove('onboarding-highlight');
      }
      
      const findElementWithRetry = (selector: string, retries: number = 3): void => {
        if (selector === 'body') {
          // Default position for body or general steps
          setTooltipPosition({ 
            top: window.innerHeight / 2, 
            left: window.innerWidth / 2 
          });
          setHighlightedElement(null);
          setElementFound(true);
          return;
        }

        const element = document.querySelector(selector);
        
        if (element) {
          // Scroll element into view first
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest' 
          });
          
          // Wait for scroll to complete before positioning tooltip
          setTimeout(() => {
            element.classList.add('onboarding-highlight');
            setHighlightedElement(element);
            
            const rect = element.getBoundingClientRect();
            const sidebarWidth = isCollapsed ? 56 : 256;
            
            // Calculate position relative to the sidebar
            let tooltipLeft = sidebarWidth + 20; // Always place to the right of sidebar
            let tooltipTop = rect.top + rect.height / 2;
            
            // Ensure tooltip stays within viewport
            const tooltipWidth = 320; // Approximate tooltip width
            const tooltipHeight = 200; // Approximate tooltip height
            
            // Adjust horizontal position if too close to right edge
            if (tooltipLeft + tooltipWidth > window.innerWidth - 20) {
              tooltipLeft = window.innerWidth - tooltipWidth - 20;
            }
            
            // Adjust vertical position if too close to top/bottom edges
            if (tooltipTop - tooltipHeight / 2 < 20) {
              tooltipTop = tooltipHeight / 2 + 20;
            } else if (tooltipTop + tooltipHeight / 2 > window.innerHeight - 20) {
              tooltipTop = window.innerHeight - tooltipHeight / 2 - 20;
            }
            
            setTooltipPosition({
              top: tooltipTop,
              left: tooltipLeft
            });
            setElementFound(true);
          }, 300);
        } else if (retries > 0) {
          // Retry after a short delay to allow DOM to update
          setTimeout(() => {
            findElementWithRetry(selector, retries - 1);
          }, 100);
        } else {
          // Element not found after retries, use default position next to sidebar
          const sidebarWidth = isCollapsed ? 56 : 256;
          setTooltipPosition({
            top: window.innerHeight / 2,
            left: sidebarWidth + 20
          });
          setElementFound(false);
          console.warn(`Onboarding element not found: ${selector}`);
        }
      };
      
      findElementWithRetry(step.targetSelector);
    }
  }, [isOnboardingActive, currentStep, isCollapsed]);

  const handleToggleCollapse = () => {
    setIsAnimating(true)
    setIsCollapsed((prev) => !prev)
    
    setTimeout(() => {
      setIsAnimating(false)
    }, 300)
  }

  // Updated logo click handler to navigate to appropriate dashboard
  const handleLogoClick = () => {
    const dashboardPath = getDashboardPath(accountType);
    navigate(dashboardPath);
    if (navOpened) {
      setNavOpened(false);
    }
  }

  const handleStorageClick = () => {
    navigate('/storage');
    if (navOpened) {
      setNavOpened(false);
    }
  }

  // Onboarding handlers
  const handleOnboardingNext = () => {
    if (currentStep < onboardingSteps.length - 1) {
      const nextStep = currentStep + 1;
      saveProgress(nextStep);
      
      // Navigate to next page
      const step = onboardingSteps[nextStep];
      if (step.targetPath !== '/') {
        navigate(step.targetPath);
      }
    } else {
      completeOnboarding();
      const dashboardPath = getDashboardPath(accountType);
      navigate(dashboardPath);
    }
  };

  const handleOnboardingSkip = () => {
    completeOnboarding();
    const dashboardPath = getDashboardPath(accountType);
    navigate(dashboardPath);
  };

  return (
    <>
      {/* Header - Only visible when mobile nav is closed */}
      <AnimatePresence>
        {!navOpened && (
          <motion.header 
            initial={{ y: 0 }}
            exit={{ y: -64 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="fixed top-0 left-0 right-0 z-40 h-16 border-b border-border/40 bg-background/95 backdrop-blur md:hidden"
          >
            <div className="flex h-full items-center justify-between px-4">
              <div 
                className="flex items-center gap-2 cursor-pointer" 
                onClick={handleLogoClick}
              >
                <img
                  src="/images/logo.png"
                  alt="Logo"
                  className="h-8 w-8"
                />
                <div className="flex flex-col">
                  <span className="text-sm font-semibold leading-tight">
                    Staffluent
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {getSidebarText(accountType)}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setNavOpened(true)}
                >
                  <IconMenu2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </motion.header>
        )}
      </AnimatePresence>

      {/* Sidebar - Desktop version */}
      <motion.aside
        initial={false}
        animate={{ 
          width: isCollapsed ? 56 : 256,
          transition: { duration: 0.3, ease: "easeInOut" }
        }}
        className={cn(
          'fixed top-0 left-0 z-30 h-screen border-r-2 border-r-muted bg-background',
          'hidden md:block',
          className
        )}
      >
        <div className="flex h-full flex-col">
          {/* Header with logo - Desktop */}
          <div className="flex h-16 items-center justify-between border-b border-border/40 px-4">
            <motion.div 
              className="flex items-center gap-2 cursor-pointer"
              animate={{ 
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                transition: { duration: 0.3 }
              }}
              onClick={handleLogoClick}
            >
              <img
                src="/images/logo.png"
                alt="Logo"
                className={`transition-all ${isCollapsed ? 'h-6 w-6' : 'h-8 w-8'}`}
              />
              
              <AnimatePresence mode="wait">
                {!isCollapsed && !isAnimating && (
                  <motion.div
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: "auto" }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex flex-col overflow-hidden"
                  >
                    <span className="text-sm font-semibold leading-tight">
                      Staffluent
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {getSidebarText(accountType)}
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>

          {/* Navigation */}
          <div className="flex-1 overflow-hidden">
            <Nav
              className="h-full overflow-y-auto px-3 py-4"
              closeNav={() => setNavOpened(false)}
              isCollapsed={isCollapsed}
              links={sidebarLinks}
            />
          </div>

          {/* Footer - Desktop */}
          <div className="border-t border-border/40 p-4 pb-safe">
            <motion.div
              animate={{
                justifyContent: isCollapsed ? 'center' : 'space-between',
                transition: { duration: 0.3 }
              }}
              className="flex items-center"
            >
              <AnimatePresence mode="wait">
                {!isCollapsed && !isAnimating && (
                  <motion.div
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: "auto" }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex items-center gap-2 text-xs text-muted-foreground"
                  >
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    <span>Active & Online</span>
                  </motion.div>
                )}
              </AnimatePresence>
              
              <div className="flex items-center gap-1">
                {/* Storage Icon - Only for business accounts */}
                {accountType === 'business' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    onClick={handleStorageClick}
                    title="Business Storage"
                  >
                    <HardDrive className="h-4 w-4" />
                  </Button>
                )}

                {/* Collapse Toggle */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  onClick={handleToggleCollapse}
                  title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                >
                  <motion.div
                    animate={{ 
                      rotate: isCollapsed ? 180 : 0,
                      transition: { duration: 0.3 }
                    }}
                  >
                    <IconChevronsLeft className="h-4 w-4" />
                  </motion.div>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {navOpened && (
          <motion.aside
            initial={{ x: -256 }}
            animate={{ x: 0 }}
            exit={{ x: -256 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className={cn(
              'fixed top-0 left-0 h-screen w-64 border-r-2 border-r-muted bg-background',
              'flex flex-col',
              'block md:hidden',
              className
            )}
            style={{ zIndex: 60 }}
          >
            <div className="flex flex-col h-full">
              {/* Header with logo and close button */}
              <div className="flex h-16 items-center justify-between border-b border-border/40 px-4 flex-shrink-0">
                <div 
                  className="flex items-center gap-2 cursor-pointer" 
                  onClick={handleLogoClick}
                >
                  <img
                    src="/images/logo.png"
                    alt="Logo"
                    className="h-8 w-8"
                  />
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold leading-tight">
                      Staffluent
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {getSidebarText(accountType)}
                    </span>
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setNavOpened(false)}
                >
                  <IconX className="h-4 w-4" />
                </Button>
              </div>

              {/* Different container structure based on PWA status */}
              {!isPWA ? (
                <div className="flex-1 flex flex-col overflow-auto">
                  <Nav
                    className="flex-1 px-3 py-4"
                    closeNav={() => setNavOpened(false)}
                    isCollapsed={false}
                    links={sidebarLinks}
                  />

                  <div className="border-t border-border/40 p-4 mt-auto mb-20">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <div className="h-2 w-2 rounded-full bg-green-500"></div>
                          <span>Active & Online</span>
                        </div>
                        
                        {/* Storage Icon - Only for business accounts */}
                        {accountType === 'business' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            onClick={handleStorageClick}
                            title="Business Storage"
                          >
                            <HardDrive className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-auto">
                    <Nav
                      className="px-3 py-4"
                      closeNav={() => setNavOpened(false)}
                      isCollapsed={false}
                      links={sidebarLinks}
                    />
                  </div>

                  <div className="border-t border-border/40 p-4 pb-safe flex-shrink-0">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <div className="h-2 w-2 rounded-full bg-green-500"></div>
                          <span>Active & Online</span>
                        </div>
                        
                        {/* Storage Icon - Only for business accounts */}
                        {accountType === 'business' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            onClick={handleStorageClick}
                            title="Business Storage"
                          >
                            <HardDrive className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Onboarding Tooltip */}
      {isOnboardingActive && onboardingSteps[currentStep] && (
        <OnboardingTooltip
          step={onboardingSteps[currentStep]}
          stepNumber={currentStep + 1}
          totalSteps={onboardingSteps.length}
          onNext={handleOnboardingNext}
          onSkip={handleOnboardingSkip}
          position={tooltipPosition}
          elementFound={elementFound}
        />
      )}
    </>
  )
}